import React, {
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const UserDetailsContext = createContext(undefined);

export const UserDetailsProvider = ({ children }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [account_id, setAccountId] = useState(null);
  const [version, setVersion] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const location = useLocation();

  // Extract parameters from URL
  const extractParamsFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    return {
      version: urlParams.get("version"),
      token: urlParams.get("token") || urlParams.get("deviceToken"),
      account_id: urlParams.get("account_id"),
    };
  };

  useEffect(() => {
    const { account_id, version, token } = extractParamsFromUrl();

    if (account_id) setAccountId(account_id);
    if (version) setVersion(version);

    if (token) {
      setToken(token);
      localStorage.setItem("token", token); // Save token for future requests
    } else {
      // Try getting token from localStorage
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        setToken(storedToken);
      } else {
        // No token anywhere: show spinner and redirect
        setIsRedirecting(true);
        setTimeout(() => {
          window.location.href = "https://accounts.aurumi.ai";
        }, 1200); // 1.2s spinner
      }
    }
  }, [location.search]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      setIsLoading(true);
      setError(null);

      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/sso/GetUserDetails?token=${token}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (response.status === 401) {
          throw new Error("Invalid login details.");
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch user details. Status: ${response.status}`);
        }

        const data = await response.json();

        if (!data || !data.TeamContactID || !data.BusinessID) {
          throw new Error("Invalid user details received");
        }

        setUserDetails(data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch user details.");
        setUserDetails(null);
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if token is present and not redirecting
    if (token && !isRedirecting) fetchUserDetails();
  }, [token, isRedirecting]);

  // Show spinner/white screen if redirecting (no token found)
  if (isRedirecting) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
        }}
      >
        <img src="/AurumiLogo.png" alt="Aurumi Logo" width="48" style={{ marginRight: "1rem" }} />
        <span style={{ fontSize: "1.5rem", fontWeight: 500, color: "#444" }}>Redirecting...</span>
      </div>
    );
  }

  // Error state with Sign In button
  if (!isLoading && error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center space-y-4">
        <div className="text-lg font-semibold text-red-600">
          {error}
        </div>
        <button
          className="px-4 py-2 abc text-white rounded hover:bg-blue-700"
          onClick={() => window.location.href = "https://accounts.aurumi.ai"}
        >
          Sign In
        </button>
      </div>
    );
  }

  const value = {
    userDetails,
    isLoading,
    error,
    token,
    account_id,
    version,
  };

  return (
    <UserDetailsContext.Provider value={value}>
      {children}
    </UserDetailsContext.Provider>
  );
};

export const useUserDetails = () => {
  const context = useContext(UserDetailsContext);
  if (!context) {
    throw new Error("useUserDetails must be used within a UserDetailsProvider");
  }
  return context;
};

export default UserDetailsContext;